import {inject, Injectable} from '@angular/core';
import {PlatformService} from './platform.service';
import {UserService} from './user.service';
import {NavigationEnd, Router} from '@angular/router';
import {filter, first} from 'rxjs';
import {TrackingPixelService} from './tracking-pixel.service';

/** Standard event names Meta and TikTok share. */
export type PixelEventName = 'CompleteRegistration' | 'ViewContent' | 'InitiateCheckout' | 'Purchase' | 'StartTrial';

/** `value` is in USD; give it only for an event about the subscription's price. */
export type PixelEventParams = {readonly eventId: string; readonly value: number | undefined};

type TikTokProperties = {
  readonly value: number;
  readonly currency: 'USD';
  readonly contents: readonly {
    readonly content_id: string;
    readonly content_type: string;
    readonly content_name: string;
    readonly quantity: number;
    readonly price: number;
  }[];
};

type TikTokIdentity = {readonly external_id: string; readonly email: string};

type Ttq = {
  readonly track: ((event: string, properties: TikTokProperties | Record<string, never>, options: {readonly event_id: string}) => void) | undefined;
  readonly identify: ((user: TikTokIdentity) => void) | undefined;
};

// The TikTok snippet's queue, or its SDK once loaded: an object whose methods are checked at the call.
const isTtq = (value: unknown): value is Ttq => typeof value === 'object' && value !== null;

interface QueuedEvent {
  readonly eventName: PixelEventName;
  readonly properties: TikTokProperties | Record<string, never>;
  readonly eventId: string;
}

// How long an event waits for identify before it goes out without it. Long enough for the user
// fetch on a normal load, short enough that a page left soon after doesn't lose the event.
const IDENTIFY_WAIT_MS = 3000;

@Injectable({
  providedIn: 'root'
})
export class TikTokTrackingService {
  private readonly platformService = inject(PlatformService);
  private readonly userService = inject(UserService);
  private readonly trackingPixelService = inject(TrackingPixelService);
  private readonly router = inject(Router);

  private eventQueue: QueuedEvent[] = [];
  // Hashed once the first user is known, and cleared once sent.
  private identity: TikTokIdentity | undefined;
  // Events queue until the first user is identified or the wait ends (a logged-out visitor's go out
  // then: an `unauthorized` emission can't tell them apart, as a prerendered page emits one before
  // the real user loads), and again while identify runs, so a sign-up after a logged-out start
  // carries the user.
  private state: 'waiting-for-user' | 'identifying' | 'sending' = 'waiting-for-user';

  initialize(): void {
    if (this.platformService.isServer) return;

    setTimeout(() => {
      if (this.state === 'waiting-for-user') {
        this.flushQueue();
      }
    }, IDENTIFY_WAIT_MS);

    // Binds to the first signed-in user of the app session only, by Piet's choice (see
    // amplitude-data.md, Identity). The state changes synchronously on the emission, before the
    // caller that signed the user in goes on to send its events.
    this.userService.user$.pipe(first(user => user.status !== 'unauthorized')).subscribe(async (user) => {
      this.state = 'identifying';
      this.identity = await this.hashIdentity(user.id, user.email);
      this.flushQueue();
    });
    // What waits for a page the pixels may see goes out on the next one.
    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => this.drain());
  }

  trackEvent(eventName: PixelEventName, params: PixelEventParams): void {
    if (this.platformService.isServer) return;

    const properties: QueuedEvent['properties'] = params.value === undefined ? {} : {
      value: params.value,
      currency: 'USD' as const,
      contents: [{
        content_id: 'homey_subscription',
        content_type: 'product',
        content_name: 'Homey subscription',
        quantity: 1,
        price: params.value,
      }],
    };
    const event = {eventName, properties, eventId: params.eventId};

    this.eventQueue.push(event);
    this.drain();
  }

  private flushQueue(): void {
    this.state = 'sending';
    this.drain();
  }

  /**
   * Sends the identify and the queued events, once the queue is open and only while the pixels may
   * run: these go out later than the call that queued them, when the current URL may be one the
   * pixels must not see (e.g. /callback?code=…). Until then they stay queued.
   */
  private drain(): void {
    if (this.state !== 'sending' || !this.trackingPixelService.pixelsActive()) {
      return;
    }
    const ttq = this.getTtq();
    const identity = this.identity;
    if (identity && ttq && typeof ttq.identify === 'function') {
      this.identity = undefined;
      try {
        ttq.identify(identity);
      } catch (err) {
        console.warn('TikTok identify failed', err);
      }
    }
    for (const event of this.eventQueue.splice(0)) {
      this.sendToTikTok(event);
    }
  }

  private async hashIdentity(userId: string, email: string): Promise<TikTokIdentity | undefined> {
    try {
      // Hashed here, so TikTok's script never sees the plaintext. TikTok matches on the SHA-256 of
      // the trimmed, lowercased address.
      const [externalId, hashedEmail] = await Promise.all([
        this.sha256(userId),
        this.sha256(email.trim().toLowerCase()),
      ]);
      return {external_id: externalId, email: hashedEmail};
    } catch (err) {
      // e.g. no crypto.subtle outside a secure context: the events still go out, unidentified.
      console.warn('TikTok identify failed', err);
      return undefined;
    }
  }

  private sendToTikTok({eventName, properties, eventId}: QueuedEvent): void {
    const ttq = this.getTtq();
    if (!ttq || typeof ttq.track !== 'function') return;

    try {
      ttq.track(eventName, properties, {event_id: eventId});
    } catch (err) {
      console.error('TikTok pixel track error', err);
    }
  }

  private getTtq(): Ttq | undefined {
    if (this.platformService.isServer) return undefined;
    const ttq: unknown = Reflect.get(window, 'ttq');
    return isTtq(ttq) ? ttq : undefined;
  }

  private async sha256(value: string): Promise<string> {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
}
